import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { articleContent } from '@/mocks/articleContent';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleSchema, buildBreadcrumbSchema, buildFAQSchema } from '@/lib/seo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articleContent[slug || ''];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);
  const articleMeta = articles.find((a) => a.slug === slug);

  const jsonLd = article && articleMeta
    ? [
        buildArticleSchema({
          title: article.title,
          description: articleMeta.excerpt,
          image: articleMeta.image,
          datePublished: articleMeta.date,
          category: article.category,
          slug: slug || '',
        }),
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: article.category, path: `/article/${slug}` },
        ]),
        ...(article.faqs.length > 0 ? [buildFAQSchema(article.faqs)] : []),
      ]
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 pt-20">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist yet.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Back to Homepage
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      {jsonLd.length > 0 && <JsonLd data={jsonLd} />}
      <article className="pt-20">
        <header className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to="/"
                className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Home
              </Link>
              <span className="text-foreground-400">/</span>
              <span className="text-sm text-foreground-700">{article.category}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                {article.readTime}
              </span>
              <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                {article.category}
              </span>
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <section className="prose-custom">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
              {article.intro}
            </p>

            <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-5 md:p-6 mb-10">
              <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-flash-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
                Quick Answer
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{article.quickAnswer}</p>
            </div>

            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{section.content}</p>
              </section>
            ))}

            {article.pros.length > 0 && article.cons.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                <div className="bg-secondary-50 border border-secondary-200/60 rounded-xl p-5 md:p-6">
                  <h3 className="font-heading text-base font-semibold text-secondary-800 mb-3 flex items-center gap-2">
                    <i className="ri-thumb-up-line w-5 h-5 flex items-center justify-center"></i>
                    Pros
                  </h3>
                  <ul className="space-y-2">
                    {article.pros.map((pro) => (
                      <li key={pro} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center text-secondary-600 mt-0.5 flex-shrink-0"></i>
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-primary-50 border border-primary-200/60 rounded-xl p-5 md:p-6">
                  <h3 className="font-heading text-base font-semibold text-primary-700 mb-3 flex items-center gap-2">
                    <i className="ri-thumb-down-line w-5 h-5 flex items-center justify-center"></i>
                    Cons
                  </h3>
                  <ul className="space-y-2">
                    {article.cons.map((con) => (
                      <li key={con} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-close-line w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 flex-shrink-0"></i>
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Final Verdict</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{article.conclusion}</p>
            </section>

            <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-10 text-center">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-3">
                Ready to Try Basetao Yourself?
              </h3>
              <p className="text-sm text-foreground-600 mb-4">
                Visit the official Basetao website and start exploring products with confidence.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Basetao Spreadsheet
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-background-300/60 text-foreground-700 font-medium text-sm hover:bg-background-200/50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>

            {article.faqs.length > 0 && (
              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                      <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer font-medium text-sm md:text-base text-foreground-800 hover:text-foreground-950 transition-colors">
                        {faq.q}
                        <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-3"></i>
                      </summary>
                      <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                        {faq.a}
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {relatedArticles.length > 0 && (
              <section className="border-t border-background-200/70 pt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
                    >
                      <span className="text-xs font-medium text-accent-700 bg-accent-100 px-2 py-0.5 rounded-full">{ra.category}</span>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mt-2 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{ra.readTime}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </section>
        </div>
      </article>
      <Footer />
    </>
  );
}